import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Alert } from '@mui/material';
import styles from './CompanyJoin.module.css';
import Loading from '../../components/loading/Loading';
import { AcceptInviteAPI, ValidateInviteAPI, LoginAPI } from '../../api/API';
import CustomButton from '../../components/buttons/CustomButton';
import LogoImage from '../../assets/images/logo-full.png';
import InputField from '../../components/inputfield/InputField';
import SuccessMessage from '../../components/success-message/SuccessMessage';

function CompanyJoin({ onSuccess }) {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const codeFromUrl = searchParams.get('code');
  
  // Refs for form validation
  const inputFieldNameRef = useRef(null);
  const inputFieldPasswordRef = useRef(null);
  const inputFieldConfirmPasswordRef = useRef(null);
  const inputFieldInvitationCodeRef = useRef(null);
  
  // State management
  const [loading, setLoading] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [invitationCode, setInvitationCode] = useState(codeFromUrl || '');
  const [invitationError, setInvitationError] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteCompanyName, setInviteCompanyName] = useState('');
  const [inviteInviterName, setInviteInviterName] = useState('');
  // O formulário (passo 2) só aparece depois que o convite foi carregado; com código na
  // URL, a tela mostra "Carregando o convite" até a resposta
  const [step, setStep] = useState(1);
  const [checkingInvite, setCheckingInvite] = useState(!!codeFromUrl);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [autoLoginSucceeded, setAutoLoginSucceeded] = useState(false);
  // Código da URL já consultado: o efeito roda duas vezes no StrictMode e o convite
  // deve ser consultado uma vez só
  const checkedCodeRef = useRef(null);

  // Handle URL parameters
  useEffect(() => {
    if (codeFromUrl && checkedCodeRef.current !== codeFromUrl) {
      checkedCodeRef.current = codeFromUrl;
      setInvitationCode(codeFromUrl);
      validateInviteCode(codeFromUrl);
    }
  }, [codeFromUrl]);

  const validateInviteCode = (code) => {
    setLoading(true);
    setInvitationError('');
    ValidateInviteAPI(code, (response) => {
      setLoading(false);
      setCheckingInvite(false);
      if (!response || response.error || !response.valid) {
        // Falha de rede ou convite inválido: volta para o código, com o motivo à vista
        const reason = typeof response?.message === 'string' && response.message
          ? response.message
          : 'Confira a sua conexão e tente de novo.';
        setStep(1);
        // Cancelado ou expirado: a mensagem do servidor já diz o motivo e o que fazer
        setInvitationError(response?.reason ? reason : `Não foi possível carregar o convite. ${reason}`);
        return;
      }
      
      setInviteEmail(response.email);
      setInviteCompanyName(response.companyName);
      setInviteInviterName(response.inviterName);
      setStep(2);
    });
  };

  const handleNextStep = () => {
    validateInviteCode(invitationCode);
  };

  const handleTogglePasswordVisibility = (field) => {
    if (field == 'password') {
      setShowPassword(!showPassword);
    } else {
      setShowConfirmPassword(!showConfirmPassword);
    }
  };

  // Validation functions
  const validateName = (name) => {
    if (!name) return 'O nome é obrigatório';
    if (name.trim().length < 3) return 'O nome deve ter pelo menos 3 caracteres';
    if (!/^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/.test(name)) return 'O nome deve conter apenas letras';
    return null;
  };

  const validatePassword = (password) => {
    if (!password) return 'A senha é obrigatória';
    if (password.length < 8) return 'A senha precisa ter pelo menos 8 caracteres.';
    return null;
  };

  const validateForm = () => {
    const name = inputFieldNameRef.current.GetValue();
    const password = inputFieldPasswordRef.current.GetValue();
    const confirmPassword = inputFieldConfirmPasswordRef.current.GetValue();

    // Validate name
    const nameError = validateName(name);
    if (nameError) {
      inputFieldNameRef.current.SetError(nameError);
      return false;
    }

    // Validate password
    const passwordError = validatePassword(password);
    if (passwordError) {
      inputFieldPasswordRef.current.SetError(passwordError);
      return false;
    }

    // Validate password confirmation
    if (!confirmPassword) {
      inputFieldConfirmPasswordRef.current.SetError('A confirmação de senha é obrigatória');
      return false;
    }
    if (password != confirmPassword) {
      inputFieldConfirmPasswordRef.current.SetError('As senhas não coincidem');
      return false;
    }

    return true;
  };

  const handleJoinCompany = () => {
    if (isSigningUp) return;

    if (!validateForm()) return;

    // O backend nunca retorna a senha na resposta; guardamos a digitada para o login automático
    const password = inputFieldPasswordRef.current.GetValue();

    setIsSigningUp(true);
    setLoading(true);

    window.visualLogger?.log('Aceitando convite...');

    AcceptInviteAPI(invitationCode, {
      name: inputFieldNameRef.current.GetValue(),
      password
    }, (response) => {
      if (response.error) {
        window.visualLogger?.log('Erro ao aceitar convite: ' + response.error);
        setLoading(false);
        setIsSigningUp(false);

        // Handle different types of errors
        if (response.error.includes('convite')) {
          setInvitationError(response.error);
        } else if (response.error.includes('senha')) {
          inputFieldPasswordRef.current.SetError(response.error);
        } else {
          setInvitationError(response.error);
        }
        return;
      }

      window.visualLogger?.log('Convite aceito com sucesso');

      // Automatically log in after successful invitation acceptance
      LoginAPI(response.email, password, (loginResponse) => {
        setLoading(false);
        setIsSigningUp(false);

        if (loginResponse.error) {
          window.visualLogger?.log('Erro no login automático: ' + loginResponse.error);
          // A conta já foi criada; o usuário só precisa fazer login manualmente
          setAutoLoginSucceeded(false);
          setShowSuccessMessage(true);
          return;
        }

        window.visualLogger?.log('Login automático realizado com sucesso');
        if (onSuccess) {
          onSuccess(loginResponse);
        }
        setAutoLoginSucceeded(true);
        setShowSuccessMessage(true);
      });
    });
  };

  // "Voltar" do primeiro passo: volta à tela de onde a pessoa veio; aberta direto
  // pelo link (sem histórico no app), vai para o login. /signup não existe mais.
  const goBack = () => {
    if ((window.history.state?.idx ?? 0) > 0) {
      navigate(-1);
      return;
    }
    navigate('/login');
  };

  // Render the appropriate step
  const renderStep = () => {
    if (checkingInvite) {
      return (
        <div className={styles.codeStep}>
          <div className={styles.logoContainer}>
            <img className={styles.logo} src={LogoImage} alt="Logo" />
            <h1 className={styles.title}>Carregando o convite</h1>
            <Loading />
          </div>
        </div>
      );
    }
    if (step == 1) {
      return (
        <div className={styles.codeStep}>
          <div className={styles.logoContainer}>
            <img className={styles.logo} src={LogoImage} alt="Logo" />
            <h1 className={styles.title}>Ingressar em uma empresa</h1>
            <p className={styles.subtitle}>Digite o código de convite que você recebeu</p>
          </div>

          {invitationError && <Alert severity="error">{invitationError}</Alert>}

          <div className={styles.formFields}>
            <InputField
              ref={inputFieldInvitationCodeRef}
              Label="Código de convite"
              placeholder="Digite o código de convite"
              Value={invitationCode}
              OnUpdateValue={(value) => {
                setInvitationCode(value);
                setInvitationError('');
              }}
              Mandatory={true}
            />
          </div>

          <div className={styles.actionButtons}>
            <CustomButton
              variant="outlined"
              style="secondary"
              label="Voltar"
              onClick={goBack}
            />
            <CustomButton
              variant="contained"
              style="primary"
              label={loading ? <Loading size={24} color="inherit" /> : "Continuar"}
              onClick={handleNextStep}
              disabled={loading || !invitationCode}
            />
          </div>
        </div>
      );
    } else {
      return (
        <div className={styles.formStep}>
          <div className={styles.logoContainer}>
            <img className={styles.logo} src={LogoImage} alt="Logo" />
            <h1 className={styles.title}>Entrar em {inviteCompanyName}</h1>
            <p className={styles.subtitle}>
              {inviteInviterName} convidou você para entrar na empresa {inviteCompanyName}.{' '}
              Complete seu cadastro para aceitar o convite.
            </p>
          </div>

          {invitationError && <Alert severity="error">{invitationError}</Alert>}

          <div className={styles.formFields}>
            <InputField
              ref={inputFieldNameRef}
              Label="Nome completo"
              placeholder="Digite seu nome completo"
              Mandatory={true}
            />
            
            <div className={styles.emailDisplay}>
              <label className={styles.inputLabel}>Email</label>
              <div className={styles.emailValue}>{inviteEmail}</div>
            </div>
            
            <InputField
              ref={inputFieldPasswordRef}
              Label="Senha"
              placeholder="Digite sua senha"
              type={showPassword ? "text" : "password"}
              Mandatory={true}
              endAdornment={
                <button
                  type="button"
                  className={styles.visibilityToggle}
                  onClick={() => handleTogglePasswordVisibility('password')}
                >
                  {showPassword ? "Ocultar" : "Mostrar"}
                </button>
              }
            />
            
            <InputField
              ref={inputFieldConfirmPasswordRef}
              Label="Confirmar senha"
              placeholder="Confirme sua senha"
              type={showConfirmPassword ? "text" : "password"}
              Mandatory={true}
              endAdornment={
                <button
                  type="button"
                  className={styles.visibilityToggle}
                  onClick={() => handleTogglePasswordVisibility('confirm')}
                >
                  {showConfirmPassword ? "Ocultar" : "Mostrar"}
                </button>
              }
            />
          </div>

          <div className={styles.actionButtons}>
            <CustomButton
              variant="outlined"
              style="secondary"
              label="Voltar"
              onClick={() => setStep(1)}
            />
            <CustomButton
              variant="contained"
              style="primary"
              label={loading ? <Loading size={24} color="inherit" /> : "Aceitar convite"}
              onClick={handleJoinCompany}
              disabled={loading}
            />
          </div>
        </div>
      );
    }
  };

  return (
    <div className='page-content'>
      <div className={styles.container}>
        <div className={styles.formContainer}>
          {renderStep()}
          <div className={styles.loginLink}>
            Já tem uma conta? <Link to="/login">Faça login</Link>
          </div>
        </div>
      </div>
      
      {showSuccessMessage && (
        <SuccessMessage
          title="Cadastro realizado com sucesso!"
          message={
            autoLoginSucceeded
              ? `Você agora faz parte da empresa ${inviteCompanyName}.`
              : `Você agora faz parte da empresa ${inviteCompanyName}. Por favor, faça login para continuar.`
          }
          redirectPath={autoLoginSucceeded ? '/dashboard' : '/login'}
          redirectLabel={autoLoginSucceeded ? 'Ir para o Dashboard' : 'Ir para o Login'}
          autoRedirectDelay={5}
        />
      )}
    </div>
  );
}

export default CompanyJoin;